// Which code table of the cerradura a carril paints: the 4-digit opening codes or the 8-digit
// reset ones. The keys are the ones the read returns (`LockCodes`), so no mapping is needed.
export type LockCodeRailKey = 'open' | 'reset';

// The agent's whole interaction with the block, in one slot: which carril is unfolded and how far
// its sequence has been slid from the server pointer (0 = server state, negative = back through the
// codes already given, positive = into the ones still to give).
//
// ONE slot, not one per carril, and that is the design: the two rules the screen has to honour —
// only one carril unfolded at a time, and a proposal that never survives being put away — both
// fall out of the shape instead of being enforced by extra guards. Closing a carril (or opening the
// other) drops its offset with it, so an unsaved proposal can never sit hidden behind a folded
// header waiting to be confirmed by mistake.
export interface LockCodeSelection {
  rail: LockCodeRailKey;
  offset: number;
}

export type LockCodeSelectionEvent =
  | { type: 'TOGGLE'; rail: LockCodeRailKey }
  | { type: 'SLIDE'; delta: number }
  | { type: 'RESET' };

// Both carriles folded — the state the block always opens in, and the one it returns to when the
// «Ver log buzón» disclosure is closed (this reducer lives in a component that unmounts with it).
export const initialLockCodeSelection: LockCodeSelection | null = null;

// Guarded transitions: an event with no carril open is a no-op, so a stray slide can never
// resurrect a selection. The slide is NOT clamped here — the window builder clamps the anchor and
// the UI disables the step control at the edge, and keeping the raw offset means the reducer never
// has to know how long the read's window is.
export function lockCodeSelection(
  state: LockCodeSelection | null,
  event: LockCodeSelectionEvent,
): LockCodeSelection | null {
  switch (event.type) {
    case 'TOGGLE':
      // Re-toggling the open carril folds it; toggling the other one takes the slot, which is what
      // makes the exclusivity and the discarded proposal the same line of code.
      return state?.rail === event.rail ? null : { rail: event.rail, offset: 0 };
    case 'SLIDE':
      return state === null ? null : { ...state, offset: state.offset + event.delta };
    case 'RESET':
      // «Volver al estado actual»: drops the proposal, keeps the carril unfolded — the agent is
      // still reading it, they only gave up on the move.
      return state === null ? null : { ...state, offset: 0 };
  }
}
